import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import logo from './logo.new1.png';
import baseURL from './baseURl';

const Buy = () => {
  // State management
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientNotFound, setClientNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  // State for form data
  const [formData, setFormData] = useState({
    articleType: 'langdiWeight',
    amount: [''],
    rate: [''],
    touch: [''],
    unit: 'g',
    amountGiven: '',
    name: ''
  });

  const [transactionType, setTransactionType] = useState('buy');
  const [calculatedTotal, setCalculatedTotal] = useState(0);
  const [originalTotal, setOriginalTotal] = useState(0);
  const [amountPending, setAmountPending] = useState(0);
  const [advance, setAdvance] = useState(null);
  const [useAdvance, setUseAdvance] = useState(false);
  const [advanceType, setAdvanceType] = useState('given');

  const [logoBase64, setLogoBase64] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [formData, useAdvance, advanceType]);

  useEffect(() => {
    // Set advanceType based on transaction type only if no existing advance type
    if (!advance?.advance?.type) {
      setAdvanceType(transactionType === 'buy' ? 'taken' : 'given');
    }
  }, [transactionType, advance]);

  useEffect(() => {
    const convertLogoToBase64 = async () => {
      try {
        const response = await fetch(logo);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoBase64(reader.result);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Error converting logo to base64:', error);
      }
    };
    convertLogoToBase64();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/clients`);
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (err) {
      setError('Error fetching clients');
    }
  };

  // Robust truncation utility functions
  const precisionTruncate = (value, precision = 3) => {
    if (value == null) return 0;
    // Specific handling to show 9.551 for 10.440 * 91.49 / 100
    const truncatedValue = Math.floor(value * Math.pow(10, precision)) / Math.pow(10, precision);
    return Number(truncatedValue.toFixed(precision));
  };

  const truncateToWholeNumber = (value) => {
    if (value == null) return 0;
    return Math.trunc(Number(value));
  };

  const calculateTotals = () => {
    let total = 0;
    let originalTotal = 0;
    const amountWithPureWeight = [];

    // Utility function for precise truncation to 3 decimal places
    const truncateToThreeDecimals = (value) => {
      return precisionTruncate(value, 3);
    };

    if (formData.articleType === 'cashAmount') {
      total = formData.amount.reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0);
      originalTotal = total;
    } else {
      formData.amount.forEach((amt, index) => {
        const amount = parseFloat(amt) || 0;
        const rate = parseFloat(formData.rate[index]) || 0;
        const ratePerGram = rate / 10;
        const convertedAmount = formData.unit === 'kg' ? amount * 1000 : amount;

        if (formData.articleType === 'langdiWeight') {
          const touch = parseFloat(formData.touch[index]) || 0;
          
          // Calculate pure weight with precise truncation
          const touchPercentage = touch / 100;
          const pureWeight = precisionTruncate(convertedAmount * touchPercentage);
          
          // Calculate total with whole number truncation
          const itemTotal = truncateToWholeNumber(pureWeight * ratePerGram);
          
          total += itemTotal;
          originalTotal += itemTotal;

          amountWithPureWeight.push({
            amount: convertedAmount,
            touch,
            pureWeightCalculated: pureWeight,
            total: itemTotal
          });
        } else if (formData.articleType === 'pureWeight') {
          const pureWeight = precisionTruncate(convertedAmount);
          const itemTotal = truncateToWholeNumber(pureWeight * ratePerGram);
          total += itemTotal;
          originalTotal += itemTotal;

          // Add amountWithPureWeight for pureWeight
          amountWithPureWeight.push({
            amount: convertedAmount,
            pureWeightCalculated: pureWeight,
            total: itemTotal
          });
        }
      });
    }

    // Handle advance based on type
    let advanceAmount = 0;
    if (useAdvance && advance?.advance?.cashAmount) {
      const availableAdvance = advance.advance.cashAmount || 0;
      advanceAmount = Math.min(availableAdvance, originalTotal);
      // Always subtract advance from original total
      total = originalTotal - advanceAmount;
    }

    // Update state with truncated values
    setCalculatedTotal(truncateToWholeNumber(total));
    setOriginalTotal(truncateToWholeNumber(originalTotal));
    setAmountPending(truncateToWholeNumber(total - (parseFloat(formData.amountGiven) || 0)));
    
    // If langdiWeight, set additional state
    if (formData.articleType === 'langdiWeight') {
      setFormData(prev => ({
        ...prev,
        amountWithPureWeight: amountWithPureWeight
      }));
    }

    // Return the values needed by handleSubmit
    return {
      total: truncateToWholeNumber(total),
      originalTotal: truncateToWholeNumber(originalTotal),
      advanceApplied: advanceAmount,
      amountWithPureWeight
    };
  };

  const handleChange = (e, index) => {
    const { name, value } = e.target;
    
    if (name === 'articleType') {
      // Reset arrays when changing article type, ensuring arrays are never undefined
      setFormData(prev => ({
        ...prev,
        articleType: value,
        amount: [''],
        rate: [''],
        touch: value === 'langdiWeight' ? [''] : [],
        unit: prev.unit,
        amountGiven: prev.amountGiven,
        name: prev.name
      }));
    } else if (Array.isArray(formData[name])) {
      const newArray = [...(formData[name] || [])];
      newArray[index] = value;
      setFormData(prev => ({
        ...prev,
        [name]: newArray
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    const foundClients = clients.filter(client => 
      client.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredClients(foundClients);
    setClientNotFound(foundClients.length === 0 && value.length > 0);
  };

  const handleSelectClient = async (client) => {
    console.log('Selected Client:', client);
    setFormData(prevData => ({ ...prevData, name: client.name }));
    setSearchTerm(client.name);
    setFilteredClients([]);
    setClientNotFound(false);
    
    try {
      // Fetch client's advance data
      console.log('Fetching advance for client:', client.name);
      const response = await axios.get(`${baseURL}/api/transactions/advance/cash/${client.name}`);
      
      console.log('Full Advance Response:', response);
      console.log('Advance Data:', response.data);
      
      // Modify this condition to correctly handle advance data
      if (response.data && (response.data.advance || response.data.cashAmount)) {
        const advanceData = response.data.advance || response.data;
        console.log('Advance Found:', advanceData);
        
        // Create a structure similar to what setAdvance expects
        const formattedAdvanceData = {
          advance: {
            cashAmount: advanceData.cashAmount,
            type: advanceData.type || 'taken'
          }
        };
        
        setAdvance(formattedAdvanceData);
        
        // Set advance type from client data
        if (advanceData.type) {
          console.log('Setting advance type:', advanceData.type);
          setAdvanceType(advanceData.type);
        }
      } else {
        console.log('No advance data found for client:', client.name);
        setAdvance(null);
      }
    } catch (error) {
      console.error('Error fetching advance data:', error);
      console.log('Error Details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setAdvance(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
  
    try {
      // Calculate totals first
      const calculationResults = calculateTotals();
      
      // Prepare preview data
      const previewRequestData = {
        articleType: formData.articleType,
        amount: formData.amount.map(amt => parseFloat(amt)),
        rate: formData.rate.map(r => parseFloat(r)),
        touch: formData.touch.map(t => parseFloat(t)),
        type: transactionType,
        name: formData.name,
        unit: formData.unit,
        amountGiven: parseFloat(formData.amountGiven) || 0,
        useAdvance,
        advanceType,
        advance: useAdvance ? calculationResults.advanceApplied : 0,
        date: new Date(),
        amountWithPureWeight: calculationResults.amountWithPureWeight,
        originalTotal: calculationResults.originalTotal,
        calculatedTotal: calculationResults.total
      };
  
      // Show preview modal
      setPreviewData(previewRequestData);
      setShowPreviewModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error preparing transaction');
      console.error('Transaction preview error:', err);
    } finally {
      setLoading(false);
    }
  };
  const confirmTransaction = async () => {
    if (!previewData) return;
  
    try {
      setLoading(true);
      const response = await axios.post(`${baseURL}/api/transactions/create`, previewData);
      
      // Set transaction and show modal
      setTransaction(response.data.transaction);
      setShowModal(true);
      setShowPreviewModal(false);
      setMessage('Transaction created successfully');
      
      // Reset form
      setFormData({
        articleType: 'langdiWeight',
        amount: [''],
        rate: [''],
        touch: [''],
        unit: 'g',
        amountGiven: '',
        name: ''
      });
      setUseAdvance(false);
      setAdvanceType('given');
      setSearchTerm('');
      setAdvance(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating transaction');
      console.error('Transaction error:', err);
    } finally {
      setLoading(false);
    }
  };
  const renderPreviewModal = () => {
    if (!showPreviewModal || !previewData) return null;
  
    return (
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Transaction Preview</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setShowPreviewModal(false)}
              ></button>
            </div>
            <div className="modal-body">
              {/* Transaction Details */}
              <div className="row">
                <div className="col-md-6">
                  <strong>Client Name:</strong> {previewData.name}
                </div>
                <div className="col-md-6">
                  <strong>Transaction Type:</strong> {previewData.type}
                </div>
                <div className="col-md-6">
                  <strong>Article Type:</strong> {previewData.articleType}
                </div>
                <div className="col-md-6">
                  <strong>Unit:</strong> {previewData.unit}
                </div>
              </div>
  
              {/* Preview Table */}
              <table className="table table-bordered mt-3">
                <thead>
                  <tr>
                    <th>Weight</th>
                    {previewData.articleType === 'langdiWeight' && <th>Touch</th>}
                    <th>Rate</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.amountWithPureWeight.map((item, index) => (
                    <tr key={index}>
                      <td>{previewData.amount[index]} {previewData.unit}</td>
                      {previewData.articleType === 'langdiWeight' && (
                        <td>{previewData.touch[index]}%</td>
                      )}
                      <td>₹{previewData.rate[index]}/10g</td>
                      <td>₹{item.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
  
              {/* Totals */}
              <div className="row mt-3">
                <div className="col-md-4">
                  <div className="alert alert-primary">
                    <strong>Original Total:</strong> ₹{previewData.originalTotal}
                  </div>
                </div>
                {previewData.useAdvance && (
                  <div className="col-md-4">
                    <div className="alert alert-info">
                      <strong>Advance Applied:</strong> ₹{previewData.advance}
                    </div>
                  </div>
                )}
                <div className="col-md-4">
                  <div className="alert alert-success">
                    <strong>Final Total:</strong> ₹{previewData.calculatedTotal}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowPreviewModal(false)}
                disabled={loading}
              >
                Go Back
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={confirmTransaction}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Confirm Transaction'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  const handlePrint = () => {
    if (!logoBase64) {
      console.error('Logo not yet loaded');
      return;
    }

    const printWindow = document.createElement('iframe');
    document.body.appendChild(printWindow);
  
    const doc = printWindow.contentWindow.document;
    doc.open();
    doc.write('<html><head><title>Print Transaction</title>');
    doc.write('<style>');
    doc.write('body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #f9f9f9; }');
    doc.write('.container { width: 80%; margin: auto; padding: 20px; background: #fff; border: 1px solid #ccc; border-radius: 8px; }');
    doc.write('h1 { text-align: center; font-size: 14px; margin-bottom: 20px; }');
    doc.write('.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }');
    doc.write('.logo { width: 70px; height: auto; object-fit: contain; }');
    doc.write('.table { width: 100%; border-collapse: collapse; margin-top: 20px; }');
    doc.write('.table th, .table td { border: 1px solid #000; padding: 8px; text-align: center; }');
    doc.write('.table th { background-color: #f2f2f2; }');
    doc.write('.totals { margin-top: 20px; width: 100%; border-collapse: collapse; }');
    doc.write('.total-row { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #000; }');
    doc.write('.total-row > span { padding: 8px; border-right: 1px solid #000; }');
    doc.write('.total-row > span:first-child { font-weight: bold; text-align: right; background: #f2f2f2; }');
    doc.write('.total-row > span:last-child { text-align: right; border-right: none; }');
    doc.write('.total-row:first-child { border-bottom: none; }');
    doc.write('</style>');
    doc.write('</head><body>');
  
    doc.write('<div class="container">');
    doc.write('<h1>|| SHREE GANESHAY NAMH ||</h1>');
    doc.write('<div class="header">');
    doc.write(`<img src="${logoBase64}" class="logo" alt="Logo">`);
    doc.write(`<span>Date: ${new Date(transaction.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>`);

    doc.write('</div>');
    
    doc.write('<p><strong>Receipt No:</strong> ' + (transaction.receiptNo || 'N/A') + '</p>');
    doc.write('<p><strong>Name:</strong> ' + (transaction.clientName || 'N/A') + '</p>');
    
    // Table for Transaction Details
    doc.write('<table class="table">');
    doc.write('<thead>');
    
    // Dynamic header based on article type
    if (transaction.articleType === 'pureWeight') {
      doc.write('<tr><th>Weight</th><th>Pure Weight</th><th>Rate</th><th>Amount</th></tr>');
    } else if (transaction.articleType === 'langdiWeight') {
      doc.write('<tr><th>Weight</th><th>Touch</th><th>Pure Weight</th><th>Rate</th><th>Amount</th></tr>');
    } else {
      doc.write('<tr><th>Amount</th><th>Total</th></tr>');
    }
    
    doc.write('</thead>');
    doc.write('<tbody>');
    
    // Render Rows based on article type
    if (transaction.articleType === 'pureWeight') {
      transaction.amount.forEach((amount, index) => {
        const weight = parseFloat(amount);
        const rate = parseFloat(transaction.rate[index]);
        const ratePerGram = rate / 10;
        const convertedAmount = transaction.unit === 'kg' ? weight * 1000 : weight;
        
        // Precise pure weight calculation with strict truncation
        const pureWeight = precisionTruncate(convertedAmount);
        const total = truncateToWholeNumber(pureWeight * ratePerGram);
        
        doc.write('<tr>');
        doc.write(`<td>${precisionTruncate(amount).toFixed(3)} ${transaction.unit}</td>`);
        doc.write(`<td>${pureWeight.toFixed(3)} ${transaction.unit}</td>`);
        doc.write(`<td>₹${precisionTruncate(rate).toFixed(2)}</td>`);
        doc.write(`<td>₹${total}</td>`);
        doc.write('</tr>');
      });
    } else if (transaction.articleType === 'langdiWeight' && transaction.amountWithPureWeight) {
      transaction.amountWithPureWeight.forEach((item, index) => {
        const unit = transaction.unit || 'g';
        
        doc.write('<tr>');
        doc.write(`<td>${item.amount} ${unit}</td>`);
        doc.write(`<td>${item.touch}%</td>`);
        doc.write(`<td>${precisionTruncate(item.amount * item.touch / 100).toFixed(3)} ${unit}</td>`);
        doc.write(`<td>₹${transaction.rate[index]}/10g</td>`);
        doc.write(`<td>₹${truncateToWholeNumber(precisionTruncate(item.amount * item.touch / 100) * (parseFloat(transaction.rate[index]) / 10))}</td>`);
        doc.write('</tr>');
      });
    } else if (transaction.articleType === 'cashAmount') {
      transaction.amount.forEach((amount, index) => {
        doc.write('<tr>');
        doc.write(`<td>${amount}</td>`);
        doc.write(`<td>₹${truncateToWholeNumber(transaction.totalAmounts[index])}</td>`);
        doc.write('</tr>');
      });
    }
    
    doc.write('</tbody>');
    doc.write('</table>');
    
    // Total Section
    doc.write('<div class="totals">');
    doc.write('<div class="total-row">');
    doc.write('<span>Total Amount:</span>');
    doc.write(`<span>₹${truncateToWholeNumber(transaction.total)}</span>`);
    doc.write('</div>');
    
    if (transaction.amountGiven > 0) {
      doc.write('<div class="total-row">');
      doc.write('<span>Amount ' + (transaction.type === 'buy' ? 'Given' : 'Received') + ':</span>');
      doc.write(`<span>₹${truncateToWholeNumber(transaction.amountGiven)}</span>`);
      doc.write('</div>');
      
      doc.write('<div class="total-row">');
      doc.write('<span>Pending Amount:</span>');
      doc.write(`<span>₹${truncateToWholeNumber(transaction.pendingAmount)}</span>`);
      doc.write('</div>');
    }
    
    if (transaction.advance?.cashAmount > 0) {
      doc.write('<div class="total-row">');
      doc.write('<span>Advance Applied:</span>');
      doc.write(`<span>₹${truncateToWholeNumber(transaction.advance.cashAmount)}</span>`);
      doc.write('</div>');
    }
    
    doc.write('</div>');
    doc.write('</div>');
    doc.write('</body></html>');
    doc.close();
    
    printWindow.contentWindow.focus();
    printWindow.contentWindow.print();
    document.body.removeChild(printWindow);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return 'Invalid Date';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleAddClient = async () => {
    if (!searchTerm.trim()) {
      setError('Client name cannot be empty');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${baseURL}/api/clients/register`, { 
        name: searchTerm.trim() 
      });

      if (response.status === 201) {
        const newClient = response.data;
        setClients(prev => [...prev, newClient]);
        setFilteredClients([]);
        setFormData(prev => ({ ...prev, name: newClient.name }));
        setClientNotFound(false);
        setMessage(`Client ${newClient.name} added successfully`);
        
        handleSelectClient(newClient);
      }
    } catch (error) {
      console.error('Error adding client:', error);
      setError(error.response?.data?.message || 'Error adding client');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvanceSubmit = async (e) => {
    e.preventDefault();
    try {
      // First check if client exists, if not create them
      if (!formData.name) {
        setError('Please enter a client name');
        return;
      }

      let clientExists = clients.some(client => client.name === formData.name);
      
      if (!clientExists) {
        // Create new client first
        const clientResponse = await axios.post(`${baseURL}/api/clients/register`, { 
          name: formData.name.trim() 
        });
        
        if (clientResponse.status !== 201) {
          throw new Error('Failed to create client');
        }
      }

      // Now create the advance
      const response = await axios.post(`${baseURL}/api/transactions/advance/create`, {
        name: formData.name,
        cashAmount: parseFloat(formData.advanceAmount) || 0,
        pureWeight: parseFloat(formData.pureWeight) || 0,
        type: advanceType
      });
      
      if (response.data.advance) {
        setAdvance(response.data);
        setMessage('Advance created successfully');
        alert(`Advance created successfully! Receipt No: ${response.data.receiptNo}`);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Error creating advance');
      console.error('Error:', error);
    }
  };

  const renderAdvanceType = () => {
    if (!useAdvance) return null;
    
    return (
      <div className="col-md-6">
        <div className="form-group">
          <label className="form-label">Advance Type</label>
          <div className="form-control bg-light">
            {advanceType.charAt(0).toUpperCase() + advanceType.slice(1)}
          </div>
        </div>
      </div>
    );
  };


  const renderWeightDetails = () => {
    if (!Array.isArray(formData.amount)) {
      return null;
    }

    return formData.amount.map((amt, index) => (
      <div key={index} className="row g-3 mb-3 align-items-end">
        <div className="col-md-4">
          <label htmlFor={`amount-${index}`} className="form-label">Weight</label>
          <input
            type="number"
            className="form-control"
            id={`amount-${index}`}
            name="amount"
            value={amt || ''}
            onChange={(e) => handleChange(e, index)}
            placeholder="Enter weight"
            step="0.001"
            required
          />
        </div>

        {formData.articleType === 'langdiWeight' && Array.isArray(formData.touch) && (
          <div className="col-md-4">
            <label htmlFor={`touch-${index}`} className="form-label">Touch (%)</label>
            <input
              type="number"
              className="form-control"
              id={`touch-${index}`}
              name="touch"
              value={formData.touch[index] || ''}
              onChange={(e) => handleChange(e, index)}
              placeholder="Enter touch"
              step="0.01"
              required
            />
          </div>
        )}

        {formData.articleType !== 'cashAmount' && Array.isArray(formData.rate) && (
          <div className="col-md-4">
            <label htmlFor={`rate-${index}`} className="form-label">Rate (per 10g)</label>
            <input
              type="number"
              className="form-control"
              id={`rate-${index}`}
              name="rate"
              value={formData.rate[index] || ''}
              onChange={(e) => handleChange(e, index)}
              placeholder="Enter rate"
              required
            />
          </div>
        )}

        {index > 0 && (
          <div className="col-auto">
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                const newAmount = [...formData.amount];
                const newTouch = formData.articleType === 'langdiWeight' ? [...(formData.touch || [])] : [];
                const newRate = [...(formData.rate || [])];
                newAmount.splice(index, 1);
                if (formData.articleType === 'langdiWeight') {
                  newTouch.splice(index, 1);
                }
                newRate.splice(index, 1);
                setFormData({ ...formData, amount: newAmount, touch: newTouch, rate: newRate });
              }}
            >
              Remove
            </button>
          </div>
        )}
      </div>
    ));
  };

  const renderTransactionModal = () => {
    if (!transaction || !showModal) return null;

    // Calculate colspan based on article type
    const getColSpan = () => {
      switch (transaction.articleType) {
        case 'langdiWeight':
          return 5; // Wt + Touch + Pure + Rate + Amount
        case 'pureWeight':
          return 4; // Wt + Pure + Rate + Amount
        case 'cashAmount':
          return 2; // Amount + Total
        default:
          return 4;
      }
    };

    // Function to render transaction rows
    const renderTransactionRows = () => {
      if (transaction.articleType === 'pureWeight') {
        return transaction.amount.map((amount, index) => (
          <tr key={index}>
            <td>{precisionTruncate(amount).toFixed(3)} {transaction.unit}</td>
            <td>{precisionTruncate(amount).toFixed(3)} {transaction.unit}</td>
            <td>₹{precisionTruncate(transaction.rate[index]).toFixed(2)}</td>
            <td>₹{truncateToWholeNumber(precisionTruncate(amount) * (parseFloat(transaction.rate[index]) / 10))}</td>
          </tr>
        ));
      } else if (transaction.articleType === 'langdiWeight' && transaction.amountWithPureWeight) {
        return transaction.amountWithPureWeight.map((item, index) => (
          <tr key={index}>
            <td>{item.amount} {transaction.unit}</td>
            <td>{item.touch}%</td>
            <td>{precisionTruncate(item.amount * item.touch / 100).toFixed(3)} {transaction.unit}</td>
            <td>₹{transaction.rate[index]}/10g</td>
            <td>₹{truncateToWholeNumber(precisionTruncate(item.amount * item.touch / 100) * (parseFloat(transaction.rate[index]) / 10))}</td>
          </tr>
        ));
      } else if (transaction.articleType === 'cashAmount') {
        return transaction.amount.map((amount, index) => (
          <tr key={index}>
            <td>{amount}</td>
            <td>₹{truncateToWholeNumber(transaction.totalAmounts[index])}</td>
          </tr>
        ));
      }
      return null;
    };

    return (
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Transaction Receipt</h5>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
            </div>
            <div className="modal-body">
              <div className="text-center mb-3">
                <h6>|| SHREE GANESHAY NAMH ||</h6>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <img 
                  src={logoBase64 || logo} 
                  alt="Logo" 
                  style={{ 
                    width: '70px', 
                    height: 'auto',
                    objectFit: 'contain'
                  }} 
                />
                <span>Date: {formatDate(transaction.date)}</span>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Receipt No:</strong> {transaction.receiptNo}
                </div>
                <div className="col-md-6">
                  <strong>Name:</strong> {transaction.clientName}
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>Weight</th>
                      {transaction.articleType === 'langdiWeight' && <th>Touch</th>}
                      {transaction.articleType !== 'cashAmount' && <th>Pure Weight</th>}
                      {transaction.articleType !== 'cashAmount' && <th>Rate</th>}
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderTransactionRows()}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td colSpan={getColSpan() - 1} className="text-end">
                        <strong>Total Amount</strong>
                      </td>
                      <td>₹{truncateToWholeNumber(transaction.total)}</td>
                    </tr>
                    {transaction.amountGiven > 0 && (
                      <>
                        <tr>
                          <td colSpan={getColSpan() - 1} className="text-end">
                            <strong>Amount {transaction.type === 'buy' ? 'Given' : 'Received'}</strong>
                          </td>
                          <td>₹{truncateToWholeNumber(transaction.amountGiven)}</td>
                        </tr>
                        <tr>
                          <td colSpan={getColSpan() - 1} className="text-end">
                            <strong>Pending Amount</strong>
                          </td>
                          <td>₹{truncateToWholeNumber(transaction.pendingAmount)}</td>
                        </tr>
                      </>
                    )}
                    {transaction.advance?.cashAmount > 0 && (
                      <tr>
                        <td colSpan={getColSpan() - 1} className="text-end">
                          <strong>Advance Applied</strong>
                        </td>
                        <td>₹{truncateToWholeNumber(transaction.advance.cashAmount)}</td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
              <button type="button" className="btn btn-primary" onClick={handlePrint}>Print Receipt</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container mt-5">
      <h1 className="mb-4">Buy or Sell Transaction</h1>
      <form onSubmit={handleSubmit}>
        <div className="row g-3">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="clientSearch" className="form-label">Client Name</label>
              <input
                type="text"
                id="clientSearch"
                value={searchTerm}
                onChange={handleSearchChange}
                className="form-control"
                placeholder="Search Client"
                required
              />
              {searchTerm && (
                <ul className="list-group mt-2 position-absolute" style={{ zIndex: 1000, width: '95%' }}>
                  {loading ? (
                    <li className="list-group-item">Loading...</li>
                  ) : filteredClients.length > 0 ? (
                    filteredClients.map(client => (
                      <li
                        key={client._id}
                        className="list-group-item list-group-item-action"
                        onClick={() => handleSelectClient(client)}
                        style={{ cursor: 'pointer' }}
                      >
                        {client.name}
                      </li>
                    ))
                  ) : (
                    clientNotFound && (
                      <li className="list-group-item text-muted">
                        No clients found. <span style={{ cursor: 'pointer', color: '#007bff' }} onClick={handleAddClient}>Click here to add a client</span>.
                      </li>
                    )
                  )}
                </ul>
              )}
            </div>
          </div>

          <div className="col-md-3">
            <div className="form-group">
              <label htmlFor="type" className="form-label">Transaction Type</label>
              <select
                id="type"
                name="type"
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                className="form-select"
                required
              >
                <option value="buy">Buy</option>
                <option value="sell">Sell</option>
              </select>
            </div>
          </div>

          <div className="col-md-3">
            <div className="form-group">
              <label htmlFor="articleType" className="form-label">Article Type</label>
              <select
                id="articleType"
                name="articleType"
                value={formData.articleType}
                onChange={(e) => handleChange(e)}
                className="form-select"
                required
              >
                <option value="langdiWeight">Langdi Weight</option>
                <option value="pureWeight">Pure Weight</option>
                <option value="cashAmount">Cash Amount</option>
              </select>
            </div>
          </div>

          <div className="col-md-3">
            <div className="form-group">
              <label htmlFor="weightUnit" className="form-label">Weight Unit</label>
              <select
                id="weightUnit"
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                className="form-select"
              >
                <option value="g">Grams</option>
                <option value="kg">Kilograms</option>
              </select>
            </div>
          </div>

          <div className="col-12">
            <div className="card mb-3">
              <div className="card-body">
                <h5 className="card-title">Weight Details</h5>
                {renderWeightDetails()}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setFormData(prevData => ({
                    ...prevData,
                    amount: Array.isArray(prevData.amount) ? [...prevData.amount, ''] : [''],
                    touch: formData.articleType === 'langdiWeight' ? 
                      (Array.isArray(prevData.touch) ? [...prevData.touch, ''] : ['']) : 
                      (prevData.touch || []),
                    rate: Array.isArray(prevData.rate) ? [...prevData.rate, ''] : ['']
                  }))}
                >
                  Add Row
                </button>
              </div>
            </div>
          </div>

          {(advance || formData.name) && (
            <div className="col-12">
              <div className="card mb-3">
                <div className="card-body">
                  <h5 className="card-title">Advance Details</h5>
                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="useAdvance"
                      checked={useAdvance}
                      onChange={(e) => setUseAdvance(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="useAdvance">Use Advance</label>
                  </div>
                  {useAdvance && renderAdvanceType()}
                  {advance && (
                    <div className="col-12 mt-2">
                      <div className="alert alert-info mb-0">
                        <strong>Available Advance:</strong> ₹{advance.advance?.cashAmount || 0}
                        <br />
                        <small className="text-muted">
                          This amount will be subtracted from the total
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="col-12">
            <div className="card mb-3">
              <div className="card-body">
                <h5 className="card-title">Payment Details</h5>
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="alert alert-primary mb-0">
                      <strong>Original Total:</strong> ₹{truncateToWholeNumber(originalTotal)}
                    </div>
                  </div>
                  {useAdvance && advance && advance.advance?.cashAmount > 0 && (
                    <div className="col-md-4">
                      <div className="alert alert-info mb-0">
                        <strong>Advance Subtracted:</strong> ₹{truncateToWholeNumber(Math.min(advance.advance?.cashAmount || 0, originalTotal))}
                      </div>
                    </div>
                  )}
                  <div className="col-md-4">
                    <div className="alert alert-success mb-0">
                      <strong>Final Total:</strong> ₹{truncateToWholeNumber(calculatedTotal)}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="amountGiven" className="form-label">Amount Given</label>
                    <input
                      type="number"
                      className="form-control"
                      id="amountGiven"
                      name="amountGiven"
                      value={formData.amountGiven}
                      onChange={handleChange}
                      placeholder="Enter amount given"
                    />
                  </div>
                  <div className="col-md-4">
                    <div className="alert alert-warning mb-0">
                      <strong>Pending Amount:</strong> ₹{truncateToWholeNumber(amountPending)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="d-grid gap-2 col-md-6 mx-auto">
          <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </>
            ) : (
              'Create Transaction'
            )}
          </button>
        </div>
      </form>

      {error && <div className="alert alert-danger mt-3">{error}</div>}
      {message && <div className="alert alert-success mt-3">{message}</div>}
 {/* Add preview modal render */}
 {renderPreviewModal()}
      {renderTransactionModal()}
    </div>
  );
};

export default Buy;
